import {
  chmod,
  copyFile,
  lstat,
  mkdir,
  readlink,
  rename,
  rm,
  symlink,
} from "node:fs/promises";
import path from "node:path";
import process from "node:process";
import packageJson from "../../package.json";
import {
  wakeLoopBinDir,
  wakeLoopManagedBinaryPath,
  wakeLoopManagedVersionBinaryPath,
  wakeLoopManagedVersionDir,
} from "../config/paths";
import { compareSemver } from "../config/version-state";

export type CliInstallSelfResult =
  | "installed"
  | "noop_same_version"
  | "noop_newer_present";

/**
 * Result of installing this binary into the managed layout. This is the shape
 * `wakeloop install-self --json` emits and the seam Desktop consumes, so it is
 * treated as a frozen contract (covered by shape tests).
 */
export type CliInstallSelfOutcome = {
  result: CliInstallSelfResult;
  version: string;
  platform: string;
  // Absolute path of the versioned binary: <root>/versions/<version>/wakeloop.
  managedBinaryPath: string;
  // Absolute path of the current-version pointer: <root>/bin/wakeloop.
  currentPointerPath: string;
  // Version the current pointer resolved to before this run, if any.
  previousVersion: string | null;
  forced: boolean;
};

export function resolveCliPlatformToken(): string {
  return `${process.platform}-${process.arch}`;
}

export function resolveCliSelfVersion(): string {
  return packageJson.version;
}

async function pathExists(target: string): Promise<boolean> {
  return await lstat(target).then(
    () => true,
    () => false
  );
}

// The version the current pointer resolves to, read from its symlink target's
// versions/<version>/ segment. Returns null when there is no pointer yet or it
// does not resolve into the managed layout.
async function readCurrentPointerVersion(): Promise<string | null> {
  const pointerPath = wakeLoopManagedBinaryPath();
  const target = await readlink(pointerPath).catch(() => null);
  if (!target) {
    return null;
  }
  const resolved = path.isAbsolute(target)
    ? target
    : path.resolve(path.dirname(pointerPath), target);
  const version = path.basename(path.dirname(resolved));
  return version.length > 0 ? version : null;
}

/**
 * Install the currently-running binary (process.execPath) into the managed
 * layout and point bin/wakeloop at it. Idempotent with semver arbitration:
 * a same-or-newer pointer is left in place (install only goes up), unless
 * `force` is set. Both the versioned binary and the pointer are swapped in
 * atomically via a temp name + rename.
 */
export async function performManagedInstall(input?: {
  force?: boolean;
  // Defaults to process.execPath (this running binary); injectable for tests.
  sourceBinaryPath?: string;
}): Promise<CliInstallSelfOutcome> {
  const force = input?.force ?? false;
  const version = resolveCliSelfVersion();
  const platform = resolveCliPlatformToken();
  const currentPointerPath = wakeLoopManagedBinaryPath();
  const managedBinaryPath = wakeLoopManagedVersionBinaryPath(version);
  const previousVersion = await readCurrentPointerVersion();

  const baseOutcome = {
    version,
    platform,
    managedBinaryPath,
    currentPointerPath,
    previousVersion,
    forced: force,
  };

  if (previousVersion !== null) {
    const comparison = compareSemver(version, previousVersion);
    if (!force && comparison === 0 && (await pathExists(managedBinaryPath))) {
      return { ...baseOutcome, result: "noop_same_version" };
    }
    if (comparison < 0 && !force) {
      if (!(await pathExists(managedBinaryPath))) {
        await placeVersionedBinary(
          input?.sourceBinaryPath ?? process.execPath,
          version,
          managedBinaryPath
        );
      }
      return { ...baseOutcome, result: "noop_newer_present" };
    }
  }

  await placeVersionedBinary(
    input?.sourceBinaryPath ?? process.execPath,
    version,
    managedBinaryPath
  );
  await swapCurrentPointer(currentPointerPath, managedBinaryPath);

  return { ...baseOutcome, result: "installed" };
}

async function placeVersionedBinary(
  sourceBinary: string,
  version: string,
  managedBinaryPath: string
): Promise<void> {
  await mkdir(wakeLoopManagedVersionDir(version), { recursive: true });
  const tmpPath = `${managedBinaryPath}.tmp-${process.pid}-${Date.now()}`;
  await rm(tmpPath, { force: true }).catch(() => undefined);
  await copyFile(sourceBinary, tmpPath);
  await chmod(tmpPath, 0o755);
  await rename(tmpPath, managedBinaryPath);
}

async function swapCurrentPointer(
  currentPointerPath: string,
  managedBinaryPath: string
): Promise<void> {
  await mkdir(wakeLoopBinDir(), { recursive: true });
  const relativeTarget = path.relative(
    path.dirname(currentPointerPath),
    managedBinaryPath
  );
  const tmpPath = `${currentPointerPath}.tmp-${process.pid}-${Date.now()}`;
  await rm(tmpPath, { force: true }).catch(() => undefined);
  await symlink(relativeTarget, tmpPath);
  await rename(tmpPath, currentPointerPath);
}

export function buildInstallSelfJsonPayload(outcome: CliInstallSelfOutcome) {
  return {
    schemaVersion: 1 as const,
    type: "cli.install_self" as const,
    result: outcome.result,
    version: outcome.version,
    platform: outcome.platform,
    managedBinaryPath: outcome.managedBinaryPath,
    currentPointerPath: outcome.currentPointerPath,
    previousVersion: outcome.previousVersion,
    forced: outcome.forced,
  };
}
